import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, roleHome } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Waves } from "lucide-react";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const { site } = useSite();
  const isLogin = mode === "login";
  const [form, setForm] = useState({ name: "", business_name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const name = site?.site_name || "LaundryDiary";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let user;
      if (isLogin) {
        user = await login(form.email, form.password);
      } else {
        if (!form.business_name.trim()) { setBusy(false); return toast.error("Enter your business name"); }
        user = await register({ name: form.name, business_name: form.business_name, email: form.email, password: form.password, phone: form.phone });
        toast.success("Your 14-day free trial has started!");
      }
      navigate(roleHome(user.role));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* left brand panel */}
      <div className="relative hidden overflow-hidden bg-slate-950 lg:block">
        <div className="pointer-events-none absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full bg-brand/30 blur-[120px]" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand"><Waves className="h-5 w-5" /></span>
            <span className="font-heading text-xl font-bold">{name}</span>
          </Link>
          <div>
            <h2 className="font-heading text-3xl font-bold leading-tight">The operating system for your commercial laundry.</h2>
            <p className="mt-4 max-w-md text-slate-300">Orders, drivers, client portals and invoicing — all in one place. Give every hotel, bar and B&B their own login.</p>
          </div>
          <p className="text-xs text-slate-500">© {name}</p>
        </div>
      </div>

      {/* form */}
      <div className="flex items-center justify-center bg-slate-50 px-5 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-8 flex items-center gap-2 lg:hidden">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-white"><Waves className="h-5 w-5" /></span>
            <span className="font-heading text-xl font-bold">{name}</span>
          </Link>
          <h1 className="font-heading text-3xl font-bold text-slate-900">{isLogin ? "Welcome back" : "Start your free trial"}</h1>
          <p className="mt-2 text-sm text-slate-500">
            {isLogin ? "Log in to your dashboard." : "Set up your laundry business in minutes. No card required."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {!isLogin && (
              <>
                <div className="space-y-1.5"><Label>Your name</Label><Input value={form.name} onChange={set("name")} required data-testid="auth-name" placeholder="Olivia Bennett" /></div>
                <div className="space-y-1.5"><Label>Business name</Label><Input value={form.business_name} onChange={set("business_name")} required data-testid="auth-business-name" placeholder="Crisp Commercial Laundry" /></div>
                <div className="space-y-1.5"><Label>Phone (optional)</Label><Input value={form.phone} onChange={set("phone")} data-testid="auth-phone" placeholder="+44…" /></div>
              </>
            )}
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="auth-email" placeholder="you@laundry.co.uk" /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={form.password} onChange={set("password")} required data-testid="auth-password" placeholder="••••••••" /></div>
            <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-brand text-base hover:bg-brand-hover" data-testid="auth-submit">
              {busy ? "Please wait…" : isLogin ? "Log in" : "Create account & start trial"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            {isLogin ? (
              <>New here? <Link to="/register" className="font-semibold text-brand" data-testid="auth-switch-register">Start a free trial</Link></>
            ) : (
              <>Already have an account? <Link to="/login" className="font-semibold text-brand" data-testid="auth-switch-login">Log in</Link></>
            )}
          </p>
          {isLogin && <p className="mt-2 text-center text-xs text-slate-400">Clients & drivers: use the login your laundry sent you.</p>}
        </div>
      </div>
    </div>
  );
}
